"use client";

import React from "react";
import { AdScripts } from "../../components/Ad/scripts";
import type { ProgrammaticFrameworks } from "../../components/Ad/types";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { useIsWebview } from "../../components/WebviewContext/hooks";
import { useCountry } from "../../hooks/useCountry";
import { usePageParams } from "../../hooks/usePageParams";
import { isSpecialCam } from "../../network/matlocq/helpers/livecams";
import { useLivecam } from "../../network/matlocq/queries/Livecam";
import { useSkiArea } from "../../network/matlocq/queries/SkiArea";

type Props = {
    children: React.ReactNode;
};

const RootTemplate: React.FC<Props> = ({ children }) => {
    const country = useCountry();
    const pageParams = usePageParams();
    const { pageType } = pageParams;
    const isWebview = useIsWebview();
    const { data: livecam, isLoading: isLoadingLivecam } = useLivecam(
        pageType?.isLivecamDetailPage && typeof pageParams.id === "string"
            ? pageParams.id
            : null
    );
    const { data: skiArea, isLoading: isLoadingSkiarea } = useSkiArea({
        id: typeof pageParams.id === "string" ? pageParams.id : null,
        enabled: pageType?.isAnySkiAreaDetailPage,
    });

    if (
        (!isLoadingLivecam && isSpecialCam(livecam ?? undefined)) ||
        (!isLoadingSkiarea && skiArea?.sponsoring.isSponsored) ||
        isWebview
    ) {
        return children;
    }

    const enabledFrameworks: Array<ProgrammaticFrameworks> = [
        "geoedge",
        "teads",
        "googleInterstitial",
    ];

    if (country === "de" || country === "ch") {
        enabledFrameworks.push("taboola");
    }

    if (pageType?.isHomePage) {
        enabledFrameworks.push("SOM");
    } else if (pageType?.isSkiAreasPage) {
        enabledFrameworks.push("SOM");
    } else if (pageType?.isAnySkiAreaDetailPage) {
        enabledFrameworks.push("SOM");
    } else if (
        pageType?.isLivecamLandingPage ||
        pageType?.isLivecamInformationPage
    ) {
        enabledFrameworks.push("SOM");
    } else if (pageType?.isLivecamDetailPage) {
        enabledFrameworks.push("taboola");
    } else if (pageType?.isAnyForecastPage) {
        enabledFrameworks.push("SOM");
    } else if (pageType?.isAnyHealthPollenDictionaryPage) {
        enabledFrameworks.push("SOM");
    }

    return (
        <>
            <ErrorBoundary>
                <AdScripts enabledFrameworks={["CP_AT", "CP_CH", "CP_DE"]} />
            </ErrorBoundary>
            {children}
            <ErrorBoundary>
                <AdScripts enabledFrameworks={enabledFrameworks} />
            </ErrorBoundary>
        </>
    );
};

export default RootTemplate;
